// batch-68 gespreksvoorbereiding (FO-346 §3.6) - toolboxbijeenkomsten als
// procedure TOOLBOX in de bestaande veiligheidsengine. Een sessie is een
// VeiligheidsChecklistInvulling (uitvoerder = ingevuldDoor) met een
// deelnemerslijst (uitgenodigd / aanwezig / afgemeld met reden) en een
// optionele handtekening per aanwezige.

import { z } from 'zod'

export const TOOLBOX_DEELNEMER_STATUSSEN = ['UITGENODIGD', 'AANWEZIG', 'AFGEMELD'] as const
export type ToolboxDeelnemerStatus = (typeof TOOLBOX_DEELNEMER_STATUSSEN)[number]

export const TOOLBOX_DEELNEMER_STATUS_LABELS: Record<ToolboxDeelnemerStatus, string> = {
  UITGENODIGD: 'Uitgenodigd',
  AANWEZIG: 'Aanwezig',
  AFGEMELD: 'Afgemeld',
}

/** Label na afronden: een nog "uitgenodigde" deelnemer is niet aanwezig gemeld. */
export function toolboxDeelnemerLabel(status: ToolboxDeelnemerStatus, sessieAfgerond: boolean): string {
  if (sessieAfgerond && status === 'UITGENODIGD') return 'Niet aanwezig'
  return TOOLBOX_DEELNEMER_STATUS_LABELS[status]
}

export const TOOLBOX_STANDAARD_SJABLOON_NAAM = 'Toolboxbijeenkomst'

const YMD = /^\d{4}-\d{2}-\d{2}$/
const id = () => z.string().trim().min(1).max(64)

export const toolboxSessieCreateSchema = z.object({
  /** Leeg = het standaard-sjabloon "Toolboxbijeenkomst". */
  templateId: id().optional(),
  onderwerp: z.string().trim().min(1, 'Vul het onderwerp in').max(200, 'Maximaal 200 tekens'),
  sessieDatum: z.string().regex(YMD, 'Kies de datum van de sessie'),
  uitvoerderEmployeeId: id(),
  locatie: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
  deelnemerEmployeeIds: z.array(id()).max(500, 'Maximaal 500 deelnemers per sessie').default([]),
})
export type ToolboxSessieCreateInput = z.infer<typeof toolboxSessieCreateSchema>

export const toolboxSessieUpdateSchema = z.object({
  onderwerp: z.string().trim().min(1, 'Vul het onderwerp in').max(200, 'Maximaal 200 tekens').optional(),
  sessieDatum: z.string().regex(YMD, 'Kies de datum van de sessie').optional(),
  uitvoerderEmployeeId: id().optional(),
  locatie: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
})
export type ToolboxSessieUpdateInput = z.infer<typeof toolboxSessieUpdateSchema>

export const toolboxDeelnemersToevoegenSchema = z.object({
  employeeIds: z.array(id()).min(1, 'Kies minimaal een medewerker').max(500, 'Maximaal 500 per keer'),
})
export type ToolboxDeelnemersToevoegenInput = z.infer<typeof toolboxDeelnemersToevoegenSchema>

export const toolboxDeelnemerUpdateSchema = z
  .object({
    status: z.enum(TOOLBOX_DEELNEMER_STATUSSEN),
    afmeldReden: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'AFGEMELD' && !(v.afmeldReden && v.afmeldReden.trim().length > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['afmeldReden'], message: 'Vul de reden van afmelden in' })
    }
  })
export type ToolboxDeelnemerUpdateInput = z.infer<typeof toolboxDeelnemerUpdateSchema>

export interface ToolboxDeelnemerDTO {
  id: string
  employeeId: string
  naam: string
  status: ToolboxDeelnemerStatus
  afmeldReden: string | null
  getekend: boolean
  getekendOp: string | null
  statusGewijzigdOp: string | null
}

export interface ToolboxSessieDTO {
  id: string
  templateId: string
  templateNaam: string | null
  onderwerp: string
  /** Geplande sessiedatum (yyyy-mm-dd); na afronden de dag van afronden als die ontbrak. */
  sessieDatum: string | null
  status: 'CONCEPT' | 'AFGEROND'
  afgerondOp: string | null
  uitvoerderEmployeeId: string
  uitvoerderNaam: string | null
  locatie: string | null
  aantalUitgenodigd: number
  aantalAanwezig: number
  aantalAfgemeld: number
  /** Alleen in het detail gevuld. */
  deelnemers: ToolboxDeelnemerDTO[]
  /** Mag de kijker deze sessie wijzigen (uitvoerder of beheer, en nog niet afgerond)? */
  magBewerken: boolean
  /** Mag de kijker uitnodigen en uitnodigingen intrekken (beheer, en nog niet afgerond)? */
  magLijstBeheren: boolean
  /** Ligt de sessiedatum nog in de toekomst? Aanwezig melden en afronden kan pas vanaf die dag. */
  nogNietBegonnen: boolean
  createdAt: string
}

/** Kandidaten om uit te nodigen (per BV of afdeling), server-side samengesteld. */
export interface ToolboxKandidaatDTO {
  employeeId: string
  naam: string
}
